import React, { useMemo } from 'react';
import { CandidatesConst, CandidateSearchFilters } from '@axiom/const';
import {
  AsyncConnector,
  BadRequestError,
  Button,
  CondensedHeader,
  CondensedLarge,
  Dropdown,
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ToastUtil,
} from '@axiom/ui';
import type { Opportunity } from '@axiom/validation';
import { AhqFindTalentSchema } from '@axiom/validation';

import type { ReadCandidatesResponseType } from '../../api/candidate-api';
import { CandidateApi } from '../../api/candidate-api';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { TalentOpportunitiesApi } from '../../api/talent-opportunities-api';
import { CandidateUtil } from '../../utils/candidate-util';

const {
  Certifying,
  InDiligence,
  Waitlist,
  PendingActive,
  Active,
  PendingBeach,
  Beach,
  Idle,
} = CandidatesConst.ProfileStatuses;

export type FindCandidateForOpportunitiesModalProps = {
  onClose: () => void;
  onSuccess?: () => void;
  opportunityIds: Array<Opportunity['id']>;
};

export const FindCandidateForOpportunitiesModal = ({
  onClose,
  onSuccess,
  opportunityIds,
}: FindCandidateForOpportunitiesModalProps) => {
  const candidatesConnector = useMemo(
    () =>
      new AsyncConnector<ReadCandidatesResponseType>({
        getData: args => {
          return CandidateApi.readCandidates({
            filters: {
              expandFa: [CandidateSearchFilters.expandFa.PRIMARY],
              axiomEmployeeType:
                CandidateUtil.getCandidatesListFilterDefaults()
                  .axiomEmployeeType,
              profileStatus: [
                Certifying,
                InDiligence,
                Waitlist,
                PendingActive,
                Active,
                PendingBeach,
                Beach,
                Idle,
              ],
              search: args.search,
            },
            page: args.page,
          });
        },
        parseData: results => {
          const candidates = results.data.map(
            ({ id, calculatedDisplayName: name }) => ({
              id,
              name,
            })
          );

          return {
            currentPage: results.meta.currentPage,
            totalPages: results.meta.pageCount,
            data: candidates,
          };
        },
      }),
    []
  );

  return (
    <Form
      name="FIND_CANDIDATE_FOR_OPPORTUNITIES_FORM"
      schema={AhqFindTalentSchema}
      initialValues={{
        candidateId: undefined,
      }}
      onSubmit={async formData => {
        const { candidateId } = formData;
        if (candidateId) {
          try {
            if (opportunityIds.length > 1) {
              await TalentOpportunitiesApi.createCandidateOpportunities({
                candidateId,
                opportunityIds,
              });
            } else if (opportunityIds[0]) {
              await OpportunityLegacyApi.createOpportunityCandidates(
                opportunityIds[0],
                { candidateIds: [candidateId] }
              );
            }
          } catch (error) {
            if (!(error instanceof BadRequestError)) throw error;
            const message =
              (error.getErrors() as { message?: string } | undefined)
                ?.message ?? 'An unknown issue occurred';
            ToastUtil.add({
              dismissible: true,
              name: 'FIND_CANDIDATE_FOR_OPPORTUNITIES_ERROR',
              type: 'error',
              children: (
                <>
                  <CondensedHeader>Error</CondensedHeader>
                  <CondensedLarge>{message}</CondensedLarge>
                </>
              ),
            });
            return;
          }
          ToastUtil.add({
            dismissible: true,
            name: 'FIND_CANDIDATE_FOR_OPPORTUNITIES_SUCCESS',
            type: 'info',
            children: <CondensedHeader>Talent added.</CondensedHeader>,
          });
          onSuccess?.();
          onClose();
        }
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="FIND_CANDIDATE_FOR_OPPORTUNITIES_MODAL">
          <ModalHeader
            name="FIND_CANDIDATE_FOR_OPPORTUNITIES_MODAL_HEADER"
            onClose={() => {
              onClose();
            }}
          >
            Search Talent
          </ModalHeader>
          <ModalSection
            name="FIND_CANDIDATE_FOR_OPPORTUNITIES_MODAL_SECTION"
            disableScrolling
          >
            <Dropdown
              name="candidateId"
              options={candidatesConnector}
              displayKey="name"
              valueKey="id"
              placeholder="Type to start searching..."
            />
          </ModalSection>
          <ModalFooter name="FIND_CANDIDATE_FOR_OPPORTUNITIES_MODAL_FOOTER">
            <Button
              onClick={() => {
                onClose();
              }}
              name="CANCEL"
              variation="outline"
            >
              Cancel
            </Button>
            <Button type="submit" onClick={fireSubmit} name="ADD">
              Add
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
